import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PackageSearch, Search } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { FullPageLoader } from '@/components/ui/Spinner';
import { getErrorMessage } from '@/lib/api-error';
import { publicUrl } from '@/lib/env';
import { buildModeloMessage, buildWaLink, fetchCatalogo, formatPrecioDesde } from './catalogo.api';
import type { CatalogoData, CatalogoFotoVariante, CatalogoProduct } from './catalogo.types';

/** Página pública del catálogo (`/c/:slug`): vitrina sin login con pedido por WhatsApp. */
export function CatalogoPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [catalogo, setCatalogo] = useState<CatalogoData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [busquedaEnviada, setBusquedaEnviada] = useState('');
  const [pagina, setPagina] = useState(1);
  const [pedido, setPedido] = useState<string | null>(null);

  // Cada cambio de negocio/búsqueda/página pide SU página al servidor (paginado,
  // 26-sep-2026): con 50-100 modelos no hay scroll infinito, son 12 por página.
  useEffect(() => {
    if (!slug) { setError('Falta el identificador del catálogo en la dirección.'); setCargando(false); return; }
    const c = new AbortController();
    setCargando(true); setError(null);
    fetchCatalogo(slug, { pagina, buscar: busquedaEnviada, signal: c.signal }).then(
      (data) => { if (!c.signal.aborted) { setCatalogo(data); setCargando(false); } },
      (e: unknown) => { if (!c.signal.aborted) { setError(getErrorMessage(e)); setCargando(false); } },
    );
    return () => c.abort();
  }, [slug, pagina, busquedaEnviada]);

  // La búsqueda reinicia en la página 1 (con 300 ms de espera mientras escriben).
  useEffect(() => {
    const t = setTimeout(() => {
      setPagina(1);
      setBusquedaEnviada(busqueda.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  // Al cambiar de página se vuelve arriba (el catálogo se lee de arriba abajo).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pagina]);

  const items = catalogo?.productos ?? [];

  if (error) {
    return (
      <main className="mx-auto flex min-h-dvh w-full max-w-3xl flex-col items-center justify-center px-4 py-10">
        <EmptyState title="No se pudo abrir el catálogo" description={error}
          action={<button type="button" onClick={() => window.location.reload()}
            className="h-10 rounded-control bg-brand-600 px-4 text-body font-medium text-white">Reintentar</button>} />
      </main>
    );
  }
  if (!catalogo && cargando) return <FullPageLoader label="Cargando el catálogo…" />;
  if (!catalogo) return null;
  const fecha = catalogo.actualizadoEn ? fechaCorta(catalogo.actualizadoEn) : null;
  const totalPaginas = Math.max(catalogo.totalPaginas, 1);
  const paginaActual = Math.min(Math.max(pagina, 1), totalPaginas);
  const desde = catalogo.total === 0 ? 0 : (paginaActual - 1) * catalogo.porPagina + 1;
  const hasta = Math.min(paginaActual * catalogo.porPagina, catalogo.total);
  return (
    <main className="min-h-dvh bg-canvas">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex w-full max-w-5xl items-center gap-3 px-4 py-4">
          {catalogo.logoUrl ? (
            <img src={publicUrl(catalogo.logoUrl)} alt={`Logo de ${catalogo.nombre}`}
              className="size-12 shrink-0 rounded-pill border border-line object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : null}
          <div className="min-w-0">
            <h1 className="truncate text-card-title font-semibold text-ink">{catalogo.nombre}</h1>
            {catalogo.descripcion ? <p className="line-clamp-2 text-caption text-ink-muted">{catalogo.descripcion}</p> : null}
            {fecha ? <p className="mt-0.5 text-micro text-ink-faint">Actualizado el {fecha}</p> : null}
          </div>
        </div>
      </header>
      <div className="mx-auto w-full max-w-5xl px-4 pb-16">
        <section aria-label="Cómo pedir" className="mt-4 rounded-panel border border-line bg-surface p-4 shadow-card">
          <h2 className="text-body font-semibold text-ink">¿Cómo pedir por este catálogo?</h2>
          <ol className="mt-2 space-y-1.5 text-caption text-ink-soft">
            <li>1️⃣ Busca el modelo que te gusta (puedes usar el buscador por nombre, marca o categoría).</li>
            <li>2️⃣ Toca el botón verde «Quiero este modelo»: se abre el WhatsApp del negocio con el mensaje ya escrito.</li>
            <li>3️⃣ Envíanos el mensaje y te confirmamos disponibilidad, talla y pago. ✅</li>
          </ol>
          <p className="mt-2 border-t border-line pt-2 text-caption text-ink-muted">¿No ves el modelo que buscas? Escríbenos y pregúntanos — aún no tenemos todos los modelos registrados en nuestro catálogo.</p>
        </section>
        <div className="mt-4">
          <Input type="search" value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre, marca o categoría…" aria-label="Buscar modelos"
            leftIcon={<Search size={16} aria-hidden="true" />} />
        </div>
        {pedido ? <div className="mt-4"><Alert tone="success">¡Listo! Te abrimos el WhatsApp con tu mensaje sobre «{pedido}». Solo dale enviar y te confirmamos disponibilidad. ✅</Alert></div> : null}
        {!catalogo.waNumber ? <div className="mt-4"><Alert tone="warning">Este negocio aún no tiene su WhatsApp conectado al catálogo. Toca cualquier modelo y te abrimos WhatsApp para que nos escribas: cuéntanos qué modelo quieres.</Alert></div> : null}
        {catalogo.total > 0 ? (
          <p className="mt-4 text-caption text-ink-muted" role="status">
            Mostrando {desde}–{hasta} de {catalogo.total} modelos
            {busquedaEnviada ? <> para «{busquedaEnviada}»</> : null}
            {totalPaginas > 1 ? <> · Página {paginaActual} de {totalPaginas}</> : null}
          </p>
        ) : null}
        {cargando ? (
          <div className="mt-4"><FullPageLoader label="Cargando modelos…" /></div>
        ) : items.length === 0 ? (
          <div className="mt-4"><EmptyState icon={<PackageSearch size={22} aria-hidden="true" />}
            title={catalogo.total === 0 ? 'Aún no hay modelos publicados' : 'Sin resultados'}
            description={catalogo.total === 0 ? 'Estamos cargando el catálogo. Escríbenos por WhatsApp y te ayudamos.' : `No encontramos modelos para «${busquedaEnviada || busqueda.trim()}». Prueba con otra palabra o pregúntanos por WhatsApp.`} /></div>
        ) : (
          <>
          <ul className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((p) => (
              <TarjetaProducto key={p.id} producto={p} waNumber={catalogo.waNumber} onPedir={setPedido} />
            ))}
          </ul>
          {totalPaginas > 1 ? (
            <nav aria-label="Páginas del catálogo" className="mt-6 flex items-center justify-center gap-2">
              <button type="button" disabled={paginaActual <= 1} onClick={() => setPagina(paginaActual - 1)}
                className="h-11 min-w-11 rounded-control border border-line bg-surface px-3 text-body font-medium text-ink disabled:opacity-40">← Anterior</button>
              <span className="px-2 text-caption text-ink-muted" role="status">Página {paginaActual} de {totalPaginas}</span>
              <button type="button" disabled={paginaActual >= totalPaginas} onClick={() => setPagina(paginaActual + 1)}
                className="h-11 min-w-11 rounded-control border border-line bg-surface px-3 text-body font-medium text-ink disabled:opacity-40">Siguiente →</button>
            </nav>
          ) : null}
          </>
        )}
      </div>
    </main>
  );
}

function TarjetaProducto({ producto: p, waNumber, onPedir }: {
  producto: CatalogoProduct;
  waNumber?: string | null;
  onPedir: (nombre: string) => void;
}) {
  const grupos: CatalogoFotoVariante[] = useMemo(() => p.fotosVariantes ?? [], [p.fotosVariantes]);
  const [grupoElegido, setGrupoElegido] = useState<string | null>(null);
  const grupoActivo = grupoElegido ? (grupos.find((g) => g.etiqueta === grupoElegido) ?? null) : null;
  const fotosGenerales = useMemo(() => p.fotos ?? (p.foto ? [p.foto] : []), [p.fotos, p.foto]);
  const fotosVisibles: Array<{ src: string; etiqueta: string | null }> = useMemo(() => {
    if (grupoActivo) return grupoActivo.fotos.map((f) => ({ src: f, etiqueta: grupoActivo.etiqueta }));
    const lista: Array<{ src: string; etiqueta: string | null }> = fotosGenerales.map((f) => ({ src: f, etiqueta: null }));
    for (const g of grupos) {
      const primera = g.fotos[0];
      if (primera) lista.push({ src: primera, etiqueta: g.etiqueta });
      for (const extra of g.fotos.slice(1)) lista.push({ src: extra, etiqueta: g.etiqueta });
    }
    return lista;
  }, [grupoActivo, fotosGenerales, grupos]);
  const variantes = p.variantes ?? [];
  const msg = buildModeloMessage(grupoActivo ? `${p.nombre} (${grupoActivo.etiqueta})` : p.nombre, p.referencia);
  const precio = formatPrecioDesde(p.precioDesde);
  const detalle = [p.marca, p.categoria].filter(Boolean).join(' · ');
  const agotado = p.disponible === false;
  return (
    <li className="flex flex-col overflow-hidden rounded-card border border-line bg-surface shadow-card">
      {fotosVisibles.length > 0 ? <Galeria fotos={fotosVisibles} alt={p.nombre} /> : <SinFoto nombre={p.nombre} />}
      <div className="flex flex-1 flex-col gap-1 p-4">
        <h3 className="text-body font-semibold text-ink">{p.nombre}</h3>
        {detalle ? <p className="text-caption text-ink-muted">{detalle}</p> : null}
        {p.referencia ? <p className="text-micro text-ink-faint">Ref: {p.referencia}</p> : null}
        {p.descripcion ? <p className="line-clamp-2 text-caption text-ink-soft">{p.descripcion}</p> : null}
        {variantes.length > 0 ? (
          <div className="mt-1 space-y-1">
            {variantes.map((v) => (
              <p key={v.nombre} className="text-caption text-ink-soft">
                <span className="font-medium text-ink">{v.nombre}:</span> {v.opciones.length > 0 ? v.opciones.join(', ') : '—'}
              </p>
            ))}
          </div>
        ) : null}
        {grupos.length > 0 ? (
          <div className="mt-2">
            <label htmlFor={`variante-${p.id}`} className="text-micro font-medium text-ink-muted">Ver fotos de:</label>
            <select id={`variante-${p.id}`} value={grupoElegido ?? ''} onChange={(e) => setGrupoElegido(e.target.value || null)}
              className="mt-1 h-10 w-full rounded-control border border-line bg-surface px-2 text-caption text-ink">
              <option value="">Todas las fotos ({fotosVisibles.length})</option>
              {grupos.map((g) => (
                <option key={g.etiqueta} value={g.etiqueta}>{g.etiqueta} ({g.fotos.length} {g.fotos.length === 1 ? 'foto' : 'fotos'})</option>
              ))}
            </select>
          </div>
        ) : fotosVisibles.length > 1 ? (
          <p className="mt-1 text-micro text-ink-faint">{fotosVisibles.length} fotos — usa las flechas para verlas todas</p>
        ) : null}
        {p.tablaPrecios && p.tablaPrecios.length > 1 ? (
          <ul className="mt-1 space-y-0.5">
            {p.tablaPrecios.map((t) => (
              <li key={t.cantidad} className="text-caption text-ink-muted">
                Desde {t.cantidad} {t.cantidad === 1 ? 'unidad' : 'unidades'}: ${t.precio.toLocaleString('es-CO', { maximumFractionDigits: 2 })}
              </li>
            ))}
          </ul>
        ) : null}
        <p className="mt-1 text-body font-semibold text-ink">{precio ?? <span className="text-caption font-normal text-ink-muted">Precio a consultar</span>}</p>
        {agotado ? <p className="text-caption font-medium text-red-600">Agotado por el momento</p> : null}
        <a href={buildWaLink(waNumber, msg)} target="_blank" rel="noopener noreferrer" onClick={() => onPedir(p.nombre)}
          className="mt-3 inline-flex h-12 w-full items-center justify-center gap-2 rounded-control bg-[#25d366] px-4 text-body font-semibold text-white hover:bg-[#1eb85a]">💬 Quiero este modelo</a>
      </div>
    </li>
  );
}

function Galeria({ fotos, alt }: { fotos: Array<{ src: string; etiqueta: string | null }>; alt: string }) {
  const [indice, setIndice] = useState(0);
  useEffect(() => { setIndice(0); }, [fotos.length]);
  const total = fotos.length;
  const actual = Math.min(indice, total - 1);
  const foto = fotos[actual];
  if (!foto) return <SinFoto nombre={alt} />;
  return (
    <div>
      <div className="relative">
        <Foto key={foto.src} src={publicUrl(foto.src)} alt={foto.etiqueta ? `${alt} (${foto.etiqueta})` : alt} />
        {total > 1 ? (
          <>
            <button type="button" aria-label="Foto anterior" onClick={() => setIndice((i) => (i - 1 + total) % total)}
              className="absolute top-1/2 left-2 flex size-9 -translate-y-1/2 items-center justify-center rounded-pill bg-black/50 text-lg text-white">‹</button>
            <button type="button" aria-label="Foto siguiente" onClick={() => setIndice((i) => (i + 1) % total)}
              className="absolute top-1/2 right-2 flex size-9 -translate-y-1/2 items-center justify-center rounded-pill bg-black/50 text-lg text-white">›</button>
            <span className="absolute right-2 bottom-2 rounded-pill bg-black/60 px-2 py-0.5 text-micro text-white" role="status">
              {actual + 1}/{total}{foto.etiqueta ? ` · ${foto.etiqueta}` : ''}
            </span>
          </>
        ) : foto.etiqueta ? (
          <span className="absolute right-2 bottom-2 rounded-pill bg-black/60 px-2 py-0.5 text-micro text-white">{foto.etiqueta}</span>
        ) : null}
      </div>
      {total > 1 ? (
        <div className="flex gap-1.5 overflow-x-auto border-t border-line bg-surface-muted/40 p-2">
          {fotos.map((f, i) => (
            <button key={`${f.src}-${i}`} type="button" aria-selected={i === actual}
              aria-label={`Ver foto ${i + 1}${f.etiqueta ? ` (${f.etiqueta})` : ''}`} onClick={() => setIndice(i)}
              className={`size-14 shrink-0 overflow-hidden rounded-control border-2 ${i === actual ? 'border-brand-600' : 'border-transparent opacity-70'}`}>
              <img src={publicUrl(f.src)} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} className="size-full object-cover" />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Foto({ src, alt }: { src: string; alt: string }) {
  const [rota, setRota] = useState(false);
  useEffect(() => { setRota(false); }, [src]);
  if (rota) return <SinFoto nombre={alt} />;
  return <img src={src} alt={alt} loading="lazy" onError={() => setRota(true)}
    className="aspect-square w-full bg-surface-muted object-cover" />;
}

function SinFoto({ nombre }: { nombre: string }) {
  return (
    <div role="img" aria-label={`Sin foto: ${nombre}`}
      className="flex aspect-square w-full flex-col items-center justify-center gap-1 bg-surface-muted text-ink-faint">
      <span className="text-3xl" aria-hidden="true">📦</span>
      <span className="text-caption">Sin foto</span>
    </div>
  );
}

function fechaCorta(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
}
